// Module 19 (BL-380) G2 - guest list helpers: names, counts, ordering, filtering. No network, no React.
import { formatPhone } from '../../../lib/phone';
import { RSVP_STATUSES } from '../eventLabels';

export const IMPORT_MAX_ROWS = 2000;   // the database refuses more than this per import
export const PLUS_ONES_MAX = 10;       // and a guest can bring at most this many
export const DEFAULT_COUNTRY = 'US';

export const BLANK_GUEST = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    group_id: '',
    rsvp_status: 'pending',
    plus_ones_allowed: 0,
    notes: '',
};

export function guestName(guest) {
    return [guest?.first_name, guest?.last_name].filter(Boolean).join(' ').trim() || 'Guest';
}

export function plusOnesOf(guests, hostId) {
    return guests.filter((guest) => guest.plus_one_of === hostId);
}

// Header numbers. "Invited" is the people on the list (plus-ones are counted as heads, not as invitations).
// Two head counts, because caterers are paid on confirmed heads and planners plan for the likely ones:
//   Confirmed heads = accepted guests + their plus-ones (a plus-one who has not declined, under a host who accepted).
//   Projected heads = everyone who has not declined, plus-ones included (a plus-one under a host who declined is not coming either).
// "X of Y expected" compares Projected with the event's expected count; the over-count warning compares Confirmed with it.
export function guestCounts(guests, expectedGuestCount) {
    const hostById = new Map(guests.map((guest) => [guest.id, guest]));
    const invited = guests.filter((guest) => !guest.plus_one_of);
    const byStatus = Object.fromEntries(RSVP_STATUSES.map((status) => [status, invited.filter((guest) => guest.rsvp_status === status).length]));
    const plusOnes = guests.length - invited.length;
    const hostOf = (guest) => hostById.get(guest.plus_one_of);
    const projectedHeads = guests.filter((guest) => (guest.plus_one_of
        ? guest.rsvp_status !== 'declined' && hostOf(guest)?.rsvp_status !== 'declined'
        : guest.rsvp_status !== 'declined')).length;
    const confirmedHeads = guests.filter((guest) => (guest.plus_one_of
        ? guest.rsvp_status !== 'declined' && hostOf(guest)?.rsvp_status === 'accepted'
        : guest.rsvp_status === 'accepted')).length;
    const expected = Number.isFinite(Number(expectedGuestCount)) && expectedGuestCount !== null && expectedGuestCount !== '' ? Number(expectedGuestCount) : null;
    return {
        invited: invited.length,
        accepted: byStatus.accepted,
        declined: byStatus.declined,
        pending: byStatus.pending,
        maybe: byStatus.maybe,
        plusOnes,
        confirmedHeads,
        projectedHeads,
        expected,
        overBy: expected !== null && confirmedHeads > expected ? confirmedHeads - expected : 0,
    };
}

// The name used to tell two guests apart: case, extra spaces and first/last split do not matter. Same rule as m19_guest_norm_name() in the database.
export function normalizeName(first, last) {
    return [first, last].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

// A duplicate is the same guest: same normalized full name AND the same email (any capitals) or the same phone (E.164).
// Returns { guest, via: 'email' | 'phone' } or null. `skipId` leaves out the guest being edited.
export function findDuplicate(values, guests, skipId = null) {
    const name = normalizeName(values.first_name, values.last_name);
    const email = String(values.email || '').trim().toLowerCase();
    const phone = String(values.phone || '').trim();
    if (!name || (!email && !phone)) return null;
    for (const other of guests) {
        if (other.id === skipId || normalizeName(other.first_name, other.last_name) !== name) continue;
        if (email && String(other.email || '').toLowerCase() === email) return { guest: other, via: 'email' };
        if (phone && other.phone === phone) return { guest: other, via: 'phone' };
    }
    return null;
}

export function duplicateMessage(via) {
    return `This guest is already on the list (same name and ${via === 'email' ? 'email address' : 'phone number'}).`;
}

// Different names sharing a phone or an email (a couple, a family) are fine. These are the plain-English notes shown next to them.
export function sharedContactNotes(values, guests, skipId = null) {
    const name = normalizeName(values.first_name, values.last_name);
    const email = String(values.email || '').trim().toLowerCase();
    const phone = String(values.phone || '').trim();
    const notes = [];
    const withEmail = email && guests.find((other) => other.id !== skipId && String(other.email || '').toLowerCase() === email && normalizeName(other.first_name, other.last_name) !== name);
    const withPhone = phone && guests.find((other) => other.id !== skipId && other.phone === phone && normalizeName(other.first_name, other.last_name) !== name);
    if (withEmail) notes.push(`Shares an email address with ${guestName(withEmail)}.`);
    if (withPhone) notes.push(`Shares a phone number with ${guestName(withPhone)}.`);
    return notes;
}

const byName = (a, b) => (a.last_name || '').localeCompare(b.last_name || '', 'en', { sensitivity: 'base' })
    || (a.first_name || '').localeCompare(b.first_name || '', 'en', { sensitivity: 'base' });

// Hosts in name order, each followed by their own plus-ones.
export function orderGuests(guests) {
    const hosts = guests.filter((guest) => !guest.plus_one_of).sort(byName);
    const ordered = [];
    hosts.forEach((host) => {
        ordered.push(host);
        ordered.push(...plusOnesOf(guests, host.id).sort(byName));
    });
    const seen = new Set(ordered.map((guest) => guest.id));
    return [...ordered, ...guests.filter((guest) => !seen.has(guest.id))];
}

const digits = (value) => String(value || '').replace(/\D/g, '');

// rsvp: 'all' | a status. group: 'all' | 'none' | a group id. A plus-one is filtered by its own RSVP and its host's group.
export function filterGuests(guests, { search = '', rsvp = 'all', group = 'all', groupsById = {} } = {}) {
    const needle = search.trim().toLowerCase();
    const needleDigits = digits(needle);
    const hostGroup = (guest) => (guest.plus_one_of ? guests.find((host) => host.id === guest.plus_one_of)?.group_id : guest.group_id) || null;
    return guests.filter((guest) => {
        if (rsvp !== 'all' && guest.rsvp_status !== rsvp) return false;
        const groupId = guest.group_id || hostGroup(guest);
        if (group === 'none' && groupId) return false;
        if (group !== 'all' && group !== 'none' && groupId !== group) return false;
        if (!needle) return true;
        const haystack = [guestName(guest), guest.email, groupsById[groupId]?.name, guest.notes].join(' ').toLowerCase();
        if (haystack.includes(needle)) return true;
        return needleDigits.length >= 3 && digits(guest.phone).includes(needleDigits);
    });
}

export function guestPhoneLabel(guest) {
    return guest?.phone ? formatPhone(guest.phone, DEFAULT_COUNTRY) : '';
}

// Plain-English version of a database error from the guest tables.
export function friendlyGuestError(error) {
    const message = String(error?.message || '');
    const code = String(error?.code || '');
    if (code === '23505' || /duplicate key/i.test(message)) {
        if (/dup_name_email/i.test(message)) return duplicateMessage('email');
        if (/dup_name_phone/i.test(message)) return duplicateMessage('phone');
        if (/group/i.test(message)) return 'There is already a group with that name.';
        return 'That guest is already on the list.';
    }
    if (code === '42501' || /row-level security/i.test(message)) return 'You do not have permission to change this guest list.';
    // The database's own guard messages are already plain English.
    if (code === '23514' && !/violates check constraint/i.test(message)) return message;
    if (/violates check constraint/i.test(message)) return 'One of the details is not valid. Check the phone number, email and the number of plus-ones (0 to 10).';
    return message || 'Something went wrong. Please try again.';
}
